// /simulations/templates/$templateId — the Authoring route for an existing
// Template, mirroring /markers/$markerId/edit. The agent is FIXED by the
// stored row, so there is no scope picker here: re-pointing a saved spec at a
// different agent would invalidate every id it holds.
//
// Prefill goes through `launchStateFromSpec`, the one mapping seam that also
// reads a spec back for Run and Duplicate. The form mounts only once the row
// has loaded and is keyed on it, so the initial state is computed once — no
// effect resets the form (React Hooks law: `useEffect` syncs with an external
// system, it does not derive state).
import { useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";

import type { components } from "../api/schema";
import { apiClient } from "../lib/apiClient";
import { ApiRequestError, requestErrorDescription } from "../lib/errors";
import { queryKeys } from "../lib/queryKeys";
import { InlineError, Page, PageHeader, Skeleton } from "../ui";
import { useLaunchOptions } from "./simulations/useLaunchOptions";
import { LoadedTemplateEditForm } from "./templates/LoadedTemplateEditForm";

type TemplateResponse = components["schemas"]["TemplateResponse"];

export function TemplateEdit() {
  const params = useParams({ strict: false });
  const templateId =
    typeof params.templateId === "string" ? params.templateId : "";
  const template = useQuery({
    enabled: templateId.length > 0,
    queryKey: queryKeys.templates.detail(templateId),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/templates/{id}", {
        params: { path: { id: templateId } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
  });

  return (
    <Page variant="config">
      {template.isPending ? (
        <>
          <PageHeader />
          <Skeleton label="Loading template" rows={8} shape="detail-field" />
        </>
      ) : template.isError ? (
        <>
          <PageHeader />
          <InlineError>{requestErrorDescription(template.error)}</InlineError>
        </>
      ) : (
        <TemplateEditForm key={template.data.id} template={template.data} />
      )}
    </Page>
  );
}

function TemplateEditForm({ template }: { template: TemplateResponse }) {
  const launchOptions = useLaunchOptions(template.agent_id);
  // The loaded form owns the header (Save/Cancel sit inside the form); the
  // route only renders one while nothing else can.
  if (launchOptions.isPending) {
    return (
      <>
        <PageHeader />
        <Skeleton
          label="Loading template options"
          rows={8}
          shape="detail-field"
        />
      </>
    );
  }
  return (
    <LoadedTemplateEditForm launchOptions={launchOptions} template={template} />
  );
}
